/**
 * cdn-domain-overview-tab.tsx — Read-only Overview tab for a CDN domain.
 *
 * Mirrors Partner's DomainOverviewTab (OverviewTab.tsx) using ops-console
 * primitives: InfoGrid + SectionLabel + MonoText from ops-detail-shared,
 * Card from @vnetwork/ui/primitives. No PageHeader, no MUI.
 *
 * Data sources:
 *   - domain detail:   GET /cdn/domains/:id          (useActAsCDNDomain)
 *   - upstream config: GET /cdn/domains/:id/upstream  (useActAsCDNUpstream)
 */

import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { InfoGrid, SectionLabel, MonoText } from '@/pages/operations/detail/tabs/ops-detail-shared';
import { useActAsCDNDomain, useActAsCDNUpstream } from '@/api/act-as-cdn';
import { formatDateTime } from '@/lib/format';
import { CdnDetailRow } from './cdn-domain-detail-row';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function fmtStatus(status: string | undefined): string {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface CdnDomainOverviewTabProps {
  domainId: string;
}

/**
 * Overview tab — read-only summary of edge status, CNAME, origin config, and
 * binding health. Two cards: Domain & Routing + Origin Config.
 */
export function CdnDomainOverviewTab({ domainId }: CdnDomainOverviewTabProps) {
  const { t } = useTranslation();
  const domainQ = useActAsCDNDomain(domainId);
  const upstreamQ = useActAsCDNUpstream(domainId);

  if (domainQ.isLoading) return <TabLoading rows={6} />;
  if (domainQ.isError) {
    return (
      <TabError
        title={t('cdn_domain.overview_error', 'Failed to load domain')}
        onRetry={() => domainQ.refetch()}
      />
    );
  }

  const domain = domainQ.data;
  if (!domain) return null;

  const cname = domain.cname_target || domain.cname || '—';

  // Upstream fields — available once the secondary query settles.
  const upstream = upstreamQ.data;
  const originUrls = upstream?.urls?.filter(Boolean) ?? [];
  const hostHeader = upstream?.host_header;
  const originMode = upstream?.mode;

  // CDN bindings summary.
  const bindings = domain.bindings ?? [];
  const activeCount = bindings.filter((b) => b.status === 'active' || b.enabled).length;
  const bindingsSummary =
    bindings.length === 0
      ? '—'
      : `${activeCount}/${bindings.length} active (${bindings.map((b) => b.provider).join(', ')})`;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

      {/* Card 1 — Domain & Routing */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cdn_domain.section_routing', 'Domain & Routing')}</CardTitle>
        </CardHeader>
        <CardBody>
          <dl
            style={{
              display: 'grid',
              gap: 'var(--vn-space-4)',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              margin: 0,
            }}
          >
            <CdnDetailRow label={t('cdn_domain.field_domain', 'Domain')} mono full>
              {domain.domain || '—'}
            </CdnDetailRow>
            <CdnDetailRow label={t('cdn_domain.field_cname', 'CNAME')} mono full>
              {cname}
            </CdnDetailRow>
            <CdnDetailRow label={t('cdn_domain.field_status', 'Status')}>
              {fmtStatus(domain.status)}
            </CdnDetailRow>
            <CdnDetailRow label={t('cdn_domain.field_enabled', 'Enabled')}>
              {domain.enabled == null ? '—' : domain.enabled ? 'Yes' : 'No'}
            </CdnDetailRow>
            <CdnDetailRow label={t('cdn_domain.field_bindings', 'CDN Bindings')} full>
              {bindingsSummary}
            </CdnDetailRow>
            {domain.tags && domain.tags.length > 0 && (
              <CdnDetailRow label={t('cdn_domain.field_tags', 'Tags')} full>
                {domain.tags.join(', ')}
              </CdnDetailRow>
            )}
            <CdnDetailRow label={t('cdn_domain.field_created', 'Created')}>
              {formatDateTime(domain.created_at)}
            </CdnDetailRow>
            <CdnDetailRow label={t('cdn_domain.field_updated', 'Last updated')}>
              {formatDateTime(domain.updated_at)}
            </CdnDetailRow>
          </dl>
        </CardBody>
      </Card>

      {/* Card 2 — Origin Config */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cdn_domain.section_origin', 'Origin Config')}</CardTitle>
        </CardHeader>
        <CardBody>
          {upstreamQ.isLoading ? (
            <TabLoading rows={3} />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              {originMode && (
                <SectionLabel>
                  {t('cdn_domain.upstream_mode', 'Mode')}: {originMode.toUpperCase()}
                </SectionLabel>
              )}
              <InfoGrid
                rows={[
                  // One row per origin URL; fall back to flat domain.origin field.
                  ...(originUrls.length > 0
                    ? originUrls.map((url, i) => ({
                        label:
                          i === 0
                            ? t('cdn_domain.field_origin', 'Origin')
                            : `${t('cdn_domain.field_origin', 'Origin')} ${i + 1}`,
                        value: <MonoText>{url}</MonoText>,
                      }))
                    : [
                        {
                          label: t('cdn_domain.field_origin', 'Origin'),
                          value: <MonoText>{(domain.origin as string) || '—'}</MonoText>,
                        },
                      ]),
                  ...(hostHeader
                    ? [
                        {
                          label: t('cdn_domain.field_host_header', 'Host header'),
                          value: <MonoText>{hostHeader}</MonoText>,
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
